/**
 * One hand-picked icon set, drawn inline.
 *
 * Inline rather than a sprite sheet or a font: there are few enough of them
 * that the bytes are trivial, and it means an icon inherits currentColor and
 * needs no second request. Every one is on a 20-unit grid with a 1.5 stroke, so
 * they sit together without anyone noticing they were drawn separately.
 */

type IconProps = { size?: number; title?: string };

function Svg({ size = 18, title, children }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 20 20"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.5}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden={title ? undefined : true}
      role={title ? 'img' : undefined}
      focusable="false"
    >
      {title && <title>{title}</title>}
      {children}
    </svg>
  );
}

export const IconText = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 5h12M10 5v11M7.5 16h5" />
  </Svg>
);

export const IconImage = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="4.5" width="14" height="11" rx="1.5" />
    <circle cx="7.5" cy="8.5" r="1.25" />
    <path d="M3.5 13.5 7 10.5l3 2.5 2.5-2 4 3.5" />
  </Svg>
);

export const IconShape = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3.5" width="9" height="9" rx="1.5" />
    <circle cx="13" cy="13" r="4" />
  </Svg>
);

/** A nib on a curve, which is what the tool draws and how every tool draws it. */
export const IconPen = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 17c0-6 4-11 9-13" />
    <path d="M12 2.5l4.5 2-2 4.5-4.5-2z" />
    <circle cx="3" cy="17" r="1.2" />
  </Svg>
);

export const IconPencil = (p: IconProps) => (
  <Svg {...p}>
    <path d="M13.5 3.5l3 3L7 16l-4 1 1-4z" />
    <path d="M11.5 5.5l3 3" />
  </Svg>
);

export const IconQr = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="5.5" height="5.5" rx="1" />
    <rect x="11.5" y="3" width="5.5" height="5.5" rx="1" />
    <rect x="3" y="11.5" width="5.5" height="5.5" rx="1" />
    <path d="M11.5 11.5h2.5v2.5M17 14v3M11.5 17h2.5" />
  </Svg>
);

export const IconLayers = (p: IconProps) => (
  <Svg {...p}>
    <path d="m10 2.75 7 3.75-7 3.75-7-3.75z" />
    <path d="m3 10.5 7 3.75 7-3.75" />
    <path d="m3 14.25 7 3.75 7-3.75" />
  </Svg>
);

export const IconTemplates = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.75" y="3.5" width="14.5" height="13" rx="1.5" />
    <path d="M2.75 7.75h14.5M7.75 7.75v8.75" />
  </Svg>
);

export const IconUndo = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7 5.5 3.5 9 7 12.5" />
    <path d="M3.5 9h7.75A4.75 4.75 0 0 1 16 13.75v.75" />
  </Svg>
);

export const IconRedo = (p: IconProps) => (
  <Svg {...p}>
    <path d="m13 5.5 3.5 3.5L13 12.5" />
    <path d="M16.5 9H8.75A4.75 4.75 0 0 0 4 13.75v.75" />
  </Svg>
);

export const IconZoomIn = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="9" r="5.25" />
    <path d="M9 6.75v4.5M6.75 9h4.5M13 13l3.75 3.75" />
  </Svg>
);

export const IconZoomOut = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="9" cy="9" r="5.25" />
    <path d="M6.75 9h4.5M13 13l3.75 3.75" />
  </Svg>
);

export const IconFit = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 7.25V4a1 1 0 0 1 1-1h3.25M12.75 3H16a1 1 0 0 1 1 1v3.25M17 12.75V16a1 1 0 0 1-1 1h-3.25M7.25 17H4a1 1 0 0 1-1-1v-3.25" />
  </Svg>
);

export const IconPreview = (p: IconProps) => (
  <Svg {...p}>
    <path d="M1.75 10S4.75 4.75 10 4.75 18.25 10 18.25 10 15.25 15.25 10 15.25 1.75 10 1.75 10Z" />
    <circle cx="10" cy="10" r="2.25" />
  </Svg>
);

export const IconLeft = (p: IconProps) => (
  <Svg {...p}>
    <path d="m12 4.5-5 5.5 5 5.5" />
  </Svg>
);

export const IconRight = (p: IconProps) => (
  <Svg {...p}>
    <path d="m8 4.5 5 5.5-5 5.5" />
  </Svg>
);

export const IconWarn = (p: IconProps) => (
  <Svg {...p}>
    <path d="M10 3.25 18 16.5H2z" />
    <path d="M10 8v3.5M10 13.75v.5" />
  </Svg>
);

export const IconReady = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="10" cy="10" r="7" />
    <path d="m6.75 10.25 2.25 2.25 4.25-4.75" />
  </Svg>
);

export const IconKeyboard = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.25" y="5.25" width="15.5" height="9.5" rx="1.5" />
    <path d="M5.5 8.5h.01M8.25 8.5h.01M11 8.5h.01M13.75 8.5h.01M6.5 11.75h7" />
  </Svg>
);

export const IconEye = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2.25 10S5 5.5 10 5.5 17.75 10 17.75 10 15 14.5 10 14.5 2.25 10 2.25 10Z" />
    <circle cx="10" cy="10" r="2" />
  </Svg>
);

export const IconEyeOff = (p: IconProps) => (
  <Svg {...p}>
    <path d="M7.5 6.15A7.6 7.6 0 0 1 10 5.75c5 0 7.75 4.25 7.75 4.25a13.7 13.7 0 0 1-2.4 2.85" />
    <path d="M5.1 7.35A13.4 13.4 0 0 0 2.25 10S5 14.25 10 14.25c1 0 1.9-.17 2.7-.45" />
    <path d="m3.5 3.5 13 13" />
  </Svg>
);

export const IconLock = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4.75" y="8.75" width="10.5" height="7.5" rx="1.5" />
    <path d="M7.25 8.75V6.5a2.75 2.75 0 0 1 5.5 0v2.25" />
  </Svg>
);

export const IconUnlock = (p: IconProps) => (
  <Svg {...p}>
    <rect x="4.75" y="8.75" width="10.5" height="7.5" rx="1.5" />
    <path d="M7.25 8.75V6.5a2.75 2.75 0 0 1 5.25-1.15" />
  </Svg>
);

export const IconBarcode = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 5.5v9M6.75 5.5v9M9.5 5.5v9M13 5.5v9M16 5.5v9" />
  </Svg>
);

export const IconGroup = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.75" y="2.75" width="8" height="8" rx="1.25" />
    <rect x="9.25" y="9.25" width="8" height="8" rx="1.25" />
  </Svg>
);

export const IconField = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.75" y="5.75" width="14.5" height="8.5" rx="1.5" strokeDasharray="3 2.5" />
    <path d="M6.5 10h7" />
  </Svg>
);

export const IconVector = (p: IconProps) => (
  <Svg {...p}>
    <path d="M4 14.5C4 9 8 5.5 16 5.5" />
    <rect x="2.25" y="13" width="3" height="3" rx="0.75" />
    <rect x="14.75" y="4" width="3" height="3" rx="0.75" />
  </Svg>
);

export const IconRuler = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.25" y="6.5" width="15.5" height="7" rx="1.25" />
    <path d="M5.75 6.5v2.5M8.5 6.5v3.5M11.25 6.5v2.5M14 6.5v3.5" />
  </Svg>
);

/* --- the rail, in Canva's order ------------------------------- */

export const IconDesign = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2.75" y="2.75" width="14.5" height="14.5" rx="2" />
    <path d="M2.75 8h14.5M8 8v9.25" />
  </Svg>
);

export const IconElements = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="6" cy="6" r="3.25" />
    <path d="M14 2.75 17.25 8.5h-6.5z" />
    <rect x="2.75" y="11" width="6.5" height="6.25" rx="1.25" />
    <path d="M14 11v6.25M10.9 14.1h6.25" />
  </Svg>
);

export const IconUpload = (p: IconProps) => (
  <Svg {...p}>
    <path d="M6 14.5H5a3.25 3.25 0 0 1-.5-6.46A4.75 4.75 0 0 1 13.75 6.5 3.5 3.5 0 0 1 15 14.5h-1" />
    <path d="M10 17V9.75M7.5 12 10 9.5l2.5 2.5" />
  </Svg>
);

export const IconDraw = (p: IconProps) => (
  <Svg {...p}>
    <path d="M13.5 3.5l3 3L8 15l-4 1 1-4z" />
    <path d="M3 18.25c2.5-1 4-.25 5.5.25s3 .5 5-.5" />
  </Svg>
);

export const IconProjects = (p: IconProps) => (
  <Svg {...p}>
    <path d="M2.75 6.25A1.5 1.5 0 0 1 4.25 4.75h3.5l1.75 2h6.25a1.5 1.5 0 0 1 1.5 1.5v6.5a1.5 1.5 0 0 1-1.5 1.5H4.25a1.5 1.5 0 0 1-1.5-1.5z" />
  </Svg>
);

export const IconFullscreen = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 7V3h4M13 3h4v4M17 13v4h-4M7 17H3v-4" />
  </Svg>
);

export const IconHelp = (p: IconProps) => (
  <Svg {...p}>
    <circle cx="10" cy="10" r="7.25" />
    <path d="M7.9 7.9a2.2 2.2 0 1 1 3.2 2c-.7.35-1.1.9-1.1 1.6v.25M10 14.25v.25" />
  </Svg>
);

export const IconPages = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="8" height="10.5" rx="1.25" />
    <path d="M13.5 6.5H16a1 1 0 0 1 1 1v8.5a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1v-.5" />
  </Svg>
);

export const IconChevron = (p: IconProps) => (
  <Svg {...p}>
    <path d="m5.5 8 4.5 4.5L14.5 8" />
  </Svg>
);

export const IconGuides = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="14" height="14" rx="1.5" />
    <path d="M7.5 1.5v17M1.5 12h17" />
  </Svg>
);

export const IconGrid = (p: IconProps) => (
  <Svg {...p}>
    <rect x="3" y="3" width="14" height="14" rx="1.5" />
    <path d="M7.75 3v14M12.25 3v14M3 7.75h14M3 12.25h14" />
  </Svg>
);
